import { supabase } from './supabase';
import { BowelMovement } from '../types';
import { getWellbeingEntriesInRange } from './wellbeingService';
import { DiseaseState, TrendLabel } from './diseaseStateConfig';

/** Mapowanie wellbeing -> objawy jelitowe dla severityScore */
function mapWellbeingToIntestinal(entry: { rumbling?: number; bloating?: number; abdominalPain?: number } | null) {
  if (!entry) return { gurgling: 0, bloating: 0, stomachPain: 0, gasLevel: 0 };
  return {
    gurgling: entry.rumbling ?? 0,
    bloating: entry.bloating ?? 0,
    stomachPain: entry.abdominalPain ?? 0,
    gasLevel: 0 // wellbeing nie ma gas – używamy 0
  };
}

// Nowy model wag aktywności (0–100)
const ACTIVITY_WEIGHTS = {
  BLOOD: 35,
  CONSISTENCY: 15,
  MUCUS: 10,
  BM_COUNT: 12,
  URGENCY: 8,
  PAIN: 10,
  INTESTINAL_SYMPTOMS: 10
};

export interface DaySummary {
  date: string;
  bmCount: number;
  bloodPresentDay: boolean;
  bloodMaxAmount?: number;
  painAvgDay: number;
  urgencyAvgDay: number;
  mucusPresentDay: boolean;
  mucusMaxAmount?: number;
  gasPresentDay: boolean;
  gasMaxIntensity?: number;
  severityScore: number;
  consistencyDistribution?: {
    płynna: number;
    miękka: number;
    normalna: number;
    twarda: number;
  };
  liquidBmCount?: number;
  liquidBmPercent?: number;
  gurgling?: number;
  bloating?: number;
  intestinalPain?: number;
  gasLevel?: number;
}

export interface PeriodMetrics {
  daysTotal: number;
  daysWithBlood: number;
  daysWithoutBlood: number;
  avgBmPerDay: number;
  avgPain: number;
  avgUrgency: number;
  avgMucusWhenPresent: number;
  avgGasIntensityWhenPresent: number;
  worstDay?: { date: string; score: number };
  bestDay?: { date: string; score: number };
  daysWithMucus?: number;
  mucusDaysPercent?: number;
  liquidStoolPercent?: number;
  avgGurgling?: number;
  avgBloating?: number;
  avgIntestinalPain?: number;
  avgGasLevel?: number;
  /** Samopoczucie (z wellbeing_entries) */
  wellbeingSummary?: {
    avgEnergy: number;
    avgStress: number;
    avgSleepHours: number;
    avgSleepQuality: number;
    daysWithData: number;
    heartburnDays: number;
    nauseaDays: number;
    jointPainDays: number;
  };
  /** Wyniki badań lab w okresie */
  labResults?: { date: string; type: string; typeOther?: string; value: number; unit?: string }[];
}

export interface DiseaseStateClassification {
  state: DiseaseState;
  stateLabel: string;
  reasons: string[];
  tendency: TrendLabel;
  tendencyLabel: string;
}

export interface DiseaseStateReportData {
  periodMetrics: PeriodMetrics;
  daySummaries: DaySummary[];
  classification: DiseaseStateClassification;
  summaryText: string;
}

/** Dane z daily_tracking do łączenia z dniem */
interface DailyTrackingData {
  gurgling: number;
  bloating: number;
  stomachPain: number;
  gasLevel: number;
}

/** Nowa funkcja: calculateActivityScore (0–100) */
function calculateActivityScore(
  day: Omit<DaySummary, 'severityScore'> & DailyTrackingData
): number {
  // 5.1 Krew (35)
  const bloodRatioDay = day.bloodPresentDay ? 1 : 0;
  const bloodAmountNormalized = (day.bloodMaxAmount ?? 0) / 5;
  const bloodScore = ACTIVITY_WEIGHTS.BLOOD * Math.max(bloodRatioDay, bloodAmountNormalized);

  // 5.2 Konsystencja (15)
  const totalBm = (day.consistencyDistribution?.płynna ?? 0) +
    (day.consistencyDistribution?.miękka ?? 0) +
    (day.consistencyDistribution?.normalna ?? 0) +
    (day.consistencyDistribution?.twarda ?? 0);
  const pctPlynnych = totalBm > 0
    ? ((day.consistencyDistribution?.płynna ?? 0) / totalBm) * 100
    : 0;
  let consistencyScore = 0;
  if (pctPlynnych > 50) consistencyScore = 15;
  else if (pctPlynnych >= 20) consistencyScore = 15 * ((pctPlynnych - 20) / 30);

  // 5.3 Śluz (10)
  const mucusScore = day.mucusPresentDay
    ? ACTIVITY_WEIGHTS.MUCUS * ((day.mucusMaxAmount ?? 0) / 5)
    : 0;

  // 5.4 Liczba wypróżnień (12)
  const bmScore = ACTIVITY_WEIGHTS.BM_COUNT * Math.min(1, day.bmCount / 8);

  // 5.5 Pilność (8) – skala 0–5
  const urgencyScore = ACTIVITY_WEIGHTS.URGENCY * (day.urgencyAvgDay / 5);

  // 5.6 Ból (10) – skala 0–5
  const painScore = ACTIVITY_WEIGHTS.PAIN * (day.painAvgDay / 5);

  // 5.7 Objawy jelitowe (10)
  const intestinalRaw =
    (day.stomachPain * 0.4) +
    (day.bloating * 0.3) +
    (day.gurgling * 0.2) +
    (day.gasLevel * 0.1);
  const intestinalScore = ACTIVITY_WEIGHTS.INTESTINAL_SYMPTOMS * Math.min(1, intestinalRaw / 5);

  const totalScore =
    bloodScore +
    consistencyScore +
    mucusScore +
    bmScore +
    urgencyScore +
    painScore +
    intestinalScore;

  return Math.round(Math.min(100, Math.max(0, totalScore)));
}

/** Zbiera kandydatów na powody i wybiera max 3 z największym wkładem */
function pickTopReasons(
  candidates: { text: string; score: number }[],
  maxCount = 3
): string[] {
  return candidates
    .filter(c => c.text && c.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxCount)
    .map(c => c.text);
}

/** Klasyfikacja stanu – nowe reguły */
function classifyDiseaseState(
  metrics: PeriodMetrics,
  periodAvgScore: number,
  last7DaysAvg: number,
  previous7DaysAvg: number,
  pctDniPlynnych: number,
  last7BloodDays: number,
  prev7BloodDays: number,
  daySummaries: DaySummary[]
): DiseaseStateClassification {
  const {
    daysWithBlood,
    daysTotal,
    avgBmPerDay,
    avgPain,
    avgUrgency,
    mucusDaysPercent = 0,
    liquidStoolPercent = 0,
    avgGurgling = 0,
    avgBloating = 0,
    avgIntestinalPain = 0,
    avgGasLevel = 0
  } = metrics;

  const bloodRatio = daysTotal > 0 ? daysWithBlood / daysTotal : 0;
  const periodBloodMaxAmount = daySummaries
    .filter(d => d.bloodPresentDay && d.bloodMaxAmount != null)
    .reduce((max, d) => Math.max(max, d.bloodMaxAmount ?? 0), 0);

  const tendency: TrendLabel =
    previous7DaysAvg > 0 && last7DaysAvg < previous7DaysAvg * 0.9 ? 'IMPROVING' :
    previous7DaysAvg > 0 && last7DaysAvg > previous7DaysAvg * 1.1 ? 'WORSENING' : 'STABLE';

  const tendencyLabels: { [key in TrendLabel]: string } = {
    IMPROVING: 'Poprawa',
    WORSENING: 'Pogorszenie',
    STABLE: 'Bez zmian'
  };

  const last7Days = daySummaries.slice(-7);
  const prev7Days = daySummaries.length > 7 ? daySummaries.slice(-14, -7) : [];
  const last7Urg = last7Days.length > 0 ? last7Days.reduce((s, d) => s + d.urgencyAvgDay, 0) / last7Days.length : 0;
  const prev7Urg = prev7Days.length > 0 ? prev7Days.reduce((s, d) => s + d.urgencyAvgDay, 0) / prev7Days.length : 0;

  const buildCandidates = (): { text: string; score: number }[] => {
    const c: { text: string; score: number }[] = [];
    if (bloodRatio > 0) c.push({ text: `Krew w ${Math.round(bloodRatio * 100)}% dni`, score: bloodRatio * 100 });
    if (avgBmPerDay > 0) c.push({ text: `Średnio ${avgBmPerDay.toFixed(1)} wypróżnień dziennie`, score: Math.min(avgBmPerDay * 15, 100) });
    if (avgPain > 0) c.push({ text: `Ból średnio ${avgPain.toFixed(1)}/5`, score: avgPain * 20 });
    if (avgUrgency > 0) c.push({ text: `Pilność średnio ${avgUrgency.toFixed(1)}/5`, score: avgUrgency * 20 });
    if (mucusDaysPercent > 0) c.push({ text: `Śluz w ${mucusDaysPercent}% dni`, score: mucusDaysPercent });
    if (liquidStoolPercent > 0) c.push({ text: `Płynna konsystencja w ${liquidStoolPercent}% wypróżnień`, score: liquidStoolPercent });
    if (avgBloating > 0) c.push({ text: `Wzdęcia średnio ${avgBloating.toFixed(1)}/5`, score: avgBloating * 20 });
    if (avgIntestinalPain > 0) c.push({ text: `Ból jelit średnio ${avgIntestinalPain.toFixed(1)}/5`, score: avgIntestinalPain * 20 });
    if (avgGurgling > 0) c.push({ text: `Przelewanie średnio ${avgGurgling.toFixed(1)}/5`, score: avgGurgling * 20 });
    if (avgGasLevel > 0) c.push({ text: `Gazy (samopoczucie) średnio ${avgGasLevel.toFixed(1)}/5`, score: avgGasLevel * 20 });
    if (pctDniPlynnych > 20) c.push({ text: `${Math.round(pctDniPlynnych)}% dni z płynną konsystencją`, score: pctDniPlynnych });
    if (last7BloodDays > prev7BloodDays) c.push({ text: 'Wzrost dni z krwią w ostatnich 7 dniach', score: 50 });
    if (last7BloodDays < prev7BloodDays && prev7BloodDays > 0) c.push({ text: 'Spadek dni z krwią w ostatnich 7 dniach', score: 40 });
    if (prev7Urg > 0 && last7Urg > prev7Urg * 1.1) c.push({ text: 'Wzrost pilności w ostatnich 7 dniach', score: 45 });
    if (prev7Urg > 0 && last7Urg < prev7Urg * 0.9) c.push({ text: 'Spadek pilności w ostatnich 7 dniach', score: 35 });
    return c;
  };

  const reasons = pickTopReasons(buildCandidates(), 3);

  if (bloodRatio >= 0.4) {
    return {
      state: 'FLARE',
      stateLabel: 'Zaostrzenie',
      reasons: reasons.length > 0 ? reasons : [`Krew w ${Math.round(bloodRatio * 100)}% dni`],
      tendency,
      tendencyLabel: tendencyLabels[tendency]
    };
  }

  if (bloodRatio >= 0.2) {
    const state = periodAvgScore >= 56 ? 'FLARE' : 'STABLE';
    return {
      state,
      stateLabel: state === 'FLARE' ? 'Zaostrzenie' : 'Stabilnie',
      reasons: reasons.length > 0 ? reasons : [`Krew w ${Math.round(bloodRatio * 100)}% dni`],
      tendency,
      tendencyLabel: tendencyLabels[tendency]
    };
  }

  if (
    periodAvgScore <= 25 &&
    bloodRatio <= 0.05 &&
    (daysWithBlood === 0 || periodBloodMaxAmount <= 1)
  ) {
    return {
      state: 'REMISSION',
      stateLabel: 'Remisja',
      reasons,
      tendency,
      tendencyLabel: tendencyLabels[tendency]
    };
  }

  if (periodAvgScore >= 56) {
    return {
      state: 'FLARE',
      stateLabel: 'Zaostrzenie',
      reasons,
      tendency,
      tendencyLabel: tendencyLabels[tendency]
    };
  }

  return {
    state: 'STABLE',
    stateLabel: 'Stabilnie',
    reasons,
    tendency,
    tendencyLabel: tendencyLabels[tendency]
  };
}

function generateSummaryText(
  classification: DiseaseStateClassification,
  metrics: PeriodMetrics,
  last7Days: DaySummary[],
  previous7Days: DaySummary[]
): string {
  const stateLabels: { [key: string]: string } = {
    REMISSION: 'remisja',
    STABLE: 'stabilny',
    FLARE: 'zaostrzenie'
  };

  const tendencyLabels: { [key: string]: string } = {
    IMPROVING: 'z tendencją poprawy',
    WORSENING: 'z tendencją pogorszenia',
    STABLE: 'bez zmian'
  };

  const last7BloodDays = last7Days.filter(d => d.bloodPresentDay).length;
  const prev7BloodDays = previous7Days.filter(d => d.bloodPresentDay).length;
  const last7AvgPain = last7Days.length > 0
    ? last7Days.reduce((sum, d) => sum + d.painAvgDay, 0) / last7Days.length
    : 0;
  const prev7AvgPain = previous7Days.length > 0
    ? previous7Days.reduce((sum, d) => sum + d.painAvgDay, 0) / previous7Days.length
    : 0;
  const last7AvgBm = last7Days.length > 0
    ? last7Days.reduce((sum, d) => sum + d.bmCount, 0) / last7Days.length
    : 0;
  const prev7AvgBm = previous7Days.length > 0
    ? previous7Days.reduce((sum, d) => sum + d.bmCount, 0) / previous7Days.length
    : 0;

  const changes: string[] = [];
  if (last7BloodDays < prev7BloodDays) changes.push('↓ krew');
  else if (last7BloodDays > prev7BloodDays) changes.push('↑ krew');
  if (last7AvgPain < prev7AvgPain - 0.5) changes.push('↓ ból');
  else if (last7AvgPain > prev7AvgPain + 0.5) changes.push('↑ ból');
  if (last7AvgBm < prev7AvgBm - 0.5) changes.push('↓ wypróżnienia');
  else if (last7AvgBm > prev7AvgBm + 0.5) changes.push('↑ wypróżnienia');

  const changesText = changes.length > 0 ? ` (${changes.join(', ')})` : '';
  return `${stateLabels[classification.state]} ${tendencyLabels[classification.tendency]}${changesText}.`;
}

export const diseaseStateReportService = {
  async getDiseaseStateReport(
    userId: string,
    dateFrom: string,
    dateTo: string
  ): Promise<DiseaseStateReportData> {
    const { data: movements, error } = await supabase
      .from('bowel_movements')
      .select('*')
      .eq('user_id', userId)
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .order('date', { ascending: true })
      .order('time', { ascending: true });

    if (error) throw error;

    let wellbeingEntries: Array<{
      date: string;
      rumbling?: number;
      bloating?: number;
      abdominalPain?: number;
      energy?: number;
      stress?: number;
      sleepHours?: number;
      sleepQuality?: number;
      heartburn?: number;
      nausea?: number;
      jointPain?: number;
    }> = [];
    try {
      const range = await getWellbeingEntriesInRange(userId, dateFrom, dateTo);
      wellbeingEntries = range.map(w => ({
        date: w.date,
        rumbling: w.rumbling,
        bloating: w.bloating,
        abdominalPain: w.abdominalPain,
        energy: w.energy,
        stress: w.stress,
        sleepHours: w.sleepHours,
        sleepQuality: w.sleepQuality,
        heartburn: w.heartburn,
        nausea: w.nausea,
        jointPain: w.jointPain
      }));
    } catch {
      // Brak wellbeing – używamy 0 dla objawów jelitowych
    }

    const trackingByDate: Record<string, { gurgling: number; bloating: number; stomachPain: number; gasLevel: number }> = {};
    for (const w of wellbeingEntries) {
      trackingByDate[w.date] = mapWellbeingToIntestinal(w);
    }

    const bowelMovements: BowelMovement[] = (movements || []).map((m: any) => ({
      id: m.id,
      date: m.date,
      time: m.time,
      consistency: m.consistency,
      hasBlood: m.has_blood,
      bloodAmount: m.blood_amount,
      bloodType: m.blood_type,
      hasMucus: m.has_mucus,
      mucusAmount: m.mucus_amount,
      pain: m.pain || 0,
      amount: m.amount,
      hasGas: m.has_gas,
      gasIntensity: m.gas_intensity,
      urgency: m.urgency || 1,
      otherSymptoms: m.other_symptoms || ''
    }));

    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);
    const days: string[] = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      days.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    let daysWithPlynna = 0;
    const daySummaries: DaySummary[] = days.map(date => {
      const dayMovements = bowelMovements.filter(bm => bm.date === date);
      const dt = trackingByDate[date] ?? { gurgling: 0, bloating: 0, stomachPain: 0, gasLevel: 0 };

      const bmCount = dayMovements.length;
      const bloodPresentDay = dayMovements.some(bm => bm.hasBlood);
      const bloodMaxAmount = bloodPresentDay
        ? Math.max(...dayMovements.filter(bm => bm.hasBlood && bm.bloodAmount).map(bm => bm.bloodAmount || 0), 0)
        : undefined;

      const painAvgDay = dayMovements.length > 0
        ? dayMovements.reduce((sum, bm) => sum + (bm.pain ?? 0), 0) / dayMovements.length
        : 0;

      const urgencyAvgDay = dayMovements.length > 0
        ? dayMovements.reduce((sum, bm) => sum + (bm.urgency ?? 1), 0) / dayMovements.length
        : 0;

      const mucusPresentDay = dayMovements.some(bm => bm.hasMucus);
      const mucusMaxAmount = mucusPresentDay
        ? Math.max(...dayMovements.filter(bm => bm.hasMucus && bm.mucusAmount).map(bm => bm.mucusAmount || 0), 0)
        : undefined;

      const gasPresentDay = dayMovements.some(bm => bm.hasGas);
      const gasMaxIntensity = gasPresentDay
        ? Math.max(...dayMovements.filter(bm => bm.hasGas && bm.gasIntensity).map(bm => bm.gasIntensity || 0), 0)
        : undefined;

      const consistencyDistribution = {
        płynna: dayMovements.filter(bm => bm.consistency === 'płynna').length,
        miękka: dayMovements.filter(bm => bm.consistency === 'miękka').length,
        normalna: dayMovements.filter(bm => bm.consistency === 'normalna').length,
        twarda: dayMovements.filter(bm => bm.consistency === 'twarda').length
      };

      if (consistencyDistribution.płynna > 0) daysWithPlynna++;

      const liquidBmCount = consistencyDistribution.płynna;
      const liquidBmPercent = bmCount > 0
        ? Math.round((liquidBmCount / bmCount) * 100)
        : 0;

      const dayBase: Omit<DaySummary, 'severityScore'> = {
        date,
        bmCount,
        bloodPresentDay,
        bloodMaxAmount,
        painAvgDay,
        urgencyAvgDay,
        mucusPresentDay,
        mucusMaxAmount,
        gasPresentDay,
        gasMaxIntensity,
        consistencyDistribution,
        liquidBmCount,
        liquidBmPercent,
        gurgling: dt.gurgling,
        bloating: dt.bloating,
        intestinalPain: dt.stomachPain,
        gasLevel: dt.gasLevel
      };

      const merged = {
        ...dayBase,
        gurgling: dt.gurgling,
        bloating: dt.bloating,
        stomachPain: dt.stomachPain,
        gasLevel: dt.gasLevel
      };

      const activityScore = calculateActivityScore(merged);
      return { ...dayBase, severityScore: activityScore };
    });

    const daysTotal = days.length;
    const daysWithBlood = daySummaries.filter(d => d.bloodPresentDay).length;
    const daysWithoutBlood = daysTotal - daysWithBlood;
    const totalBmCount = daySummaries.reduce((sum, d) => sum + d.bmCount, 0);
    const avgBmPerDay = daysTotal > 0 ? totalBmCount / daysTotal : 0;

    // Średnia bólu ze wszystkich dni (łącznie z 0) – skala 0–5
    const avgPain = daysTotal > 0
      ? daySummaries.reduce((sum, d) => sum + d.painAvgDay, 0) / daysTotal
      : 0;

    const allUrgencies = bowelMovements.map(bm => bm.urgency ?? 1);
    const avgUrgency = allUrgencies.length > 0
      ? allUrgencies.reduce((sum, u) => sum + u, 0) / allUrgencies.length
      : 0;

    const mucusAmounts = bowelMovements
      .filter(bm => bm.hasMucus && bm.mucusAmount)
      .map(bm => bm.mucusAmount || 0);
    const avgMucusWhenPresent = mucusAmounts.length > 0
      ? mucusAmounts.reduce((sum, m) => sum + m, 0) / mucusAmounts.length
      : 0;

    const gasIntensities = bowelMovements
      .filter(bm => bm.hasGas && bm.gasIntensity)
      .map(bm => bm.gasIntensity || 0);
    const avgGasIntensityWhenPresent = gasIntensities.length > 0
      ? gasIntensities.reduce((sum, g) => sum + g, 0) / gasIntensities.length
      : 0;

    const periodAvgScore = daySummaries.length > 0
      ? daySummaries.reduce((sum, d) => sum + d.severityScore, 0) / daySummaries.length
      : 0;

    const pctDniPlynnych = daysTotal > 0 ? (daysWithPlynna / daysTotal) * 100 : 0;

    const scoresWithDays = daySummaries.map(d => ({ date: d.date, score: d.severityScore }));
    const worstDay = scoresWithDays.length > 0
      ? scoresWithDays.reduce((worst, current) => current.score > worst.score ? current : worst)
      : undefined;
    const bestDay = scoresWithDays.length > 0
      ? scoresWithDays.reduce((best, current) => current.score < best.score ? current : best)
      : undefined;

    const daysWithMucus = daySummaries.filter(d => d.mucusPresentDay).length;
    const mucusDaysPercent = daysTotal > 0 ? Math.round((daysWithMucus / daysTotal) * 100) : 0;

    const totalBmInPeriod = daySummaries.reduce((sum, d) => sum + d.bmCount, 0);
    const totalLiquidBm = daySummaries.reduce((sum, d) => sum + (d.liquidBmCount ?? (d.consistencyDistribution?.płynna ?? 0)), 0);
    const liquidStoolPercent = totalBmInPeriod > 0 ? Math.round((totalLiquidBm / totalBmInPeriod) * 100) : 0;

    const avgGurgling = daysTotal > 0
      ? daySummaries.reduce((sum, d) => sum + (d.gurgling ?? 0), 0) / daysTotal
      : 0;
    const avgBloating = daysTotal > 0
      ? daySummaries.reduce((sum, d) => sum + (d.bloating ?? 0), 0) / daysTotal
      : 0;
    const avgIntestinalPain = daysTotal > 0
      ? daySummaries.reduce((sum, d) => sum + (d.intestinalPain ?? 0), 0) / daysTotal
      : 0;
    const avgGasLevel = daysTotal > 0
      ? daySummaries.reduce((sum, d) => sum + (d.gasLevel ?? 0), 0) / daysTotal
      : 0;

    // Samopoczucie (wellbeing)
    let wellbeingSummary: PeriodMetrics['wellbeingSummary'];
    if (wellbeingEntries.length > 0) {
      const energySum = wellbeingEntries.reduce((s, w) => s + (w.energy ?? 0), 0);
      const stressSum = wellbeingEntries.reduce((s, w) => s + (w.stress ?? 0), 0);
      const sleepHoursSum = wellbeingEntries.reduce((s, w) => s + (w.sleepHours ?? 0), 0);
      const sleepQualitySum = wellbeingEntries.reduce((s, w) => s + (w.sleepQuality ?? 0), 0);
      const heartburnDays = wellbeingEntries.filter(w => (w.heartburn ?? 0) > 0).length;
      const nauseaDays = wellbeingEntries.filter(w => (w.nausea ?? 0) > 0).length;
      const jointPainDays = wellbeingEntries.filter(w => (w.jointPain ?? 0) > 0).length;
      wellbeingSummary = {
        avgEnergy: wellbeingEntries.length > 0 ? energySum / wellbeingEntries.length : 0,
        avgStress: wellbeingEntries.length > 0 ? stressSum / wellbeingEntries.length : 0,
        avgSleepHours: wellbeingEntries.length > 0 ? sleepHoursSum / wellbeingEntries.length : 0,
        avgSleepQuality: wellbeingEntries.length > 0 ? sleepQualitySum / wellbeingEntries.length : 0,
        daysWithData: wellbeingEntries.length,
        heartburnDays,
        nauseaDays,
        jointPainDays
      };
    }

    // Wyniki badań lab
    const { data: labRows } = await supabase
      .from('lab_results')
      .select('date, type, type_other, value, unit')
      .eq('user_id', userId)
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .order('date', { ascending: false });
    const labResults = (labRows || []).map((r: any) => ({
      date: r.date,
      type: r.type,
      typeOther: r.type_other,
      value: Number(r.value),
      unit: r.unit
    }));

    const periodMetrics: PeriodMetrics = {
      daysTotal,
      daysWithBlood,
      daysWithoutBlood,
      avgBmPerDay,
      avgPain,
      avgUrgency,
      avgMucusWhenPresent,
      avgGasIntensityWhenPresent,
      worstDay,
      bestDay,
      daysWithMucus,
      mucusDaysPercent,
      liquidStoolPercent,
      avgGurgling,
      avgBloating,
      avgIntestinalPain,
      avgGasLevel,
      wellbeingSummary,
      labResults: labResults.length > 0 ? labResults : undefined
    };

    const sortedDays = [...daySummaries].sort((a, b) => a.date.localeCompare(b.date));
    const last7Days = sortedDays.slice(-7);
    const previous7Days = sortedDays.length > 7 ? sortedDays.slice(-14, -7) : [];

    const last7DaysAvg = last7Days.length > 0
      ? last7Days.reduce((sum, d) => sum + d.severityScore, 0) / last7Days.length
      : 0;
    const previous7DaysAvg = previous7Days.length > 0
      ? previous7Days.reduce((sum, d) => sum + d.severityScore, 0) / previous7Days.length
      : 0;

    const last7BloodDays = last7Days.filter(d => d.bloodPresentDay).length;
    const prev7BloodDays = previous7Days.filter(d => d.bloodPresentDay).length;

    const classification = classifyDiseaseState(
      periodMetrics,
      periodAvgScore,
      last7DaysAvg,
      previous7DaysAvg,
      pctDniPlynnych,
      last7BloodDays,
      prev7BloodDays,
      sortedDays
    );

    const summaryText = generateSummaryText(classification, periodMetrics, last7Days, previous7Days);

    return {
      periodMetrics,
      daySummaries,
      classification,
      summaryText
    };
  }
};
